import React, { useMemo, useState } from 'react';
import { Button, EmptyState, Input, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';

/**
 * Per-location replenishment thresholds. Edits a local map keyed by locationId,
 * then persists by calling `onSave(parLevelsArray)` (which patches the item).
 */
export default function ParLevelsTab({ item, locations = [], canManage, onSave }) {
  const toast = useToast();
  const t = useT();
  const [saving, setSaving] = useState(false);

  const initial = useMemo(() => {
    const map = {};
    (item.parLevels || []).forEach((p) => {
      map[p.locationId] = {
        parLevel: p.parLevel ?? '',
        reorderPoint: p.reorderPoint ?? '',
        reorderQuantity: p.reorderQuantity ?? '',
      };
    });
    return map;
  }, [item]);

  const [rows, setRows] = useState(initial);

  const setField = (locId, key) => (e) =>
    setRows((r) => ({ ...r, [locId]: { ...(r[locId] || {}), [key]: e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      const parLevels = Object.entries(rows)
        .map(([locationId, v]) => ({
          locationId,
          parLevel: v.parLevel === '' ? null : Number(v.parLevel),
          reorderPoint: v.reorderPoint === '' ? null : Number(v.reorderPoint),
          reorderQuantity: v.reorderQuantity === '' ? null : Number(v.reorderQuantity),
        }))
        // Only keep rows where the user entered a par or reorder point.
        .filter((p) => p.parLevel != null || p.reorderPoint != null)
        .map((p) => ({
          locationId: p.locationId,
          parLevel: p.parLevel ?? 0,
          reorderPoint: p.reorderPoint ?? 0,
          reorderQuantity: p.reorderQuantity,
        }));
      await onSave(parLevels);
      toast.success(t('Par levels saved'));
    } catch (err) {
      toast.error(t('Could not save par levels'), { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  if (locations.length === 0) {
    return (
      <EmptyState
        icon={<Icon.MapPin size={28} />}
        title={t('No locations yet')}
        description={t('Par levels are set per storage location. Add locations under Settings → Locations first.')}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div className="par-grid">
        <span className="par-grid__head">{t('Location')}</span>
        <span className="par-grid__head">{t('Par level')}</span>
        <span className="par-grid__head">{t('Reorder point')}</span>
        <span className="par-grid__head">{t('Reorder qty')}</span>
      </div>
      {locations.map((loc) => {
        const v = rows[loc.id] || {};
        return (
          <div className="par-grid" key={loc.id}>
            <div>
              <div style={{ fontWeight: 600 }}>{loc.name}</div>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{loc.kind}</div>
            </div>
            <Input type="number" placeholder="0" value={v.parLevel ?? ''} onChange={setField(loc.id, 'parLevel')} disabled={!canManage} />
            <Input type="number" placeholder="0" value={v.reorderPoint ?? ''} onChange={setField(loc.id, 'reorderPoint')} disabled={!canManage} />
            <Input type="number" placeholder="—" value={v.reorderQuantity ?? ''} onChange={setField(loc.id, 'reorderQuantity')} disabled={!canManage} />
          </div>
        );
      })}
      {canManage && (
        <div className="pp-form__actions">
          <Button variant="primary" loading={saving} onClick={save}>{t('Save par levels')}</Button>
        </div>
      )}
    </div>
  );
}
